import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { useCart } from "@/contexts/CartContext";
import getCoverImage from "@/utils/coverImages";
import PreviewDialog from "@/components/PreviewDialog";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, ShoppingCart, Check, Loader2, Eye, Gift, Mail, Palette, BookOpen, Plus } from "lucide-react";

const ColoringPages = () => {
  const [packs, setPacks] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [previewPack, setPreviewPack] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [email, setEmail] = useState("");
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [showBundleDialog, setShowBundleDialog] = useState(false);

  const { addToCart, isInCart, setIsCartOpen, cartCount, addBundleToCart, isBundleInCart } = useCart();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packsRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/packs`),
        axios.get(`${API}/bundles`)
      ]);
      setPacks(packsRes.data.packs);
      setBundles(bundlesRes.data.bundles);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (pack) => {
    addToCart(pack);
    setIsCartOpen(true);
  };

  const handleAddBundleToCart = (bundle, selectedPackIds = []) => {
    addBundleToCart(bundle, selectedPackIds);
    setIsCartOpen(true);
  };

  const handlePurchaseClick = (type, id) => {
    setPendingPurchase({ type, id });
    setShowEmailDialog(true);
  };

  const handlePurchase = async () => {
    if (!pendingPurchase) return;
    
    setPurchaseLoading(pendingPurchase.id);
    setShowEmailDialog(false);
    
    try {
      let requestBody = {
        origin_url: window.location.origin,
        email: email || undefined
      };

      if (pendingPurchase.type === "pack") {
        requestBody.pack_id = pendingPurchase.id;
      } else if (pendingPurchase.type === "bundle") {
        requestBody.bundle_id = pendingPurchase.id;
        if (pendingPurchase.id === "bundle_3") {
          requestBody.selected_packs = selectedPacks;
        }
      }

      const response = await axios.post(`${API}/checkout`, requestBody);
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert(error.response?.data?.detail || "Failed to initiate checkout. Please try again.");
      setPurchaseLoading(null);
    }
  };

  const handleBundle3Click = () => {
    setSelectedPacks([]);
    setShowBundleDialog(true);
  };

  const togglePackSelection = (packId) => {
    setSelectedPacks(prev => {
      if (prev.includes(packId)) {
        return prev.filter(id => id !== packId);
      } else if (prev.length < 3) {
        return [...prev, packId];
      }
      return prev;
    });
  };

  const confirmBundle3Selection = (addToCartMode = false) => {
    if (selectedPacks.length === 3) {
      setShowBundleDialog(false);
      if (addToCartMode) {
        handleAddBundleToCart(bundle3, selectedPacks);
      } else {
        handlePurchaseClick("bundle", "bundle_3");
      }
    }
  };

  const bundle3 = bundles.find(b => b.id === "bundle_3");
  const completeBundle = bundles.find(b => b.id === "bundle_complete");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-amber-100 to-amber-50" data-testid="coloring-hero">
        <div className="container mx-auto text-center max-w-4xl">
          <Badge className="mb-4 bg-emerald-900 text-amber-500 text-lg py-2 px-6">Digital Downloads</Badge>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-6">Bible Heroes Coloring Pages</h1>
          <p className="text-xl text-slate-600 mb-6 leading-relaxed">
            Beautiful coloring pages featuring beloved Bible heroes and stories. Each pack contains 12-15 high-quality pages 
            ready to print and color. Perfect for Sunday school, homeschool, or family devotion time!
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Badge className="bg-amber-500 text-emerald-900 text-xl py-2 px-6">$1.00 per pack</Badge>
            <Badge className="bg-slate-200 text-slate-800 text-lg py-2 px-6">
              <Download className="inline h-4 w-4 mr-2" /> Instant Download
            </Badge>
            <Badge className="bg-emerald-700 text-white text-lg py-2 px-6">
              <Mail className="inline h-4 w-4 mr-2" /> Email Delivery
            </Badge>
          </div>
        </div>
      </section>

      {/* Bundle Offers Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-emerald-900 to-emerald-800" data-testid="bundle-section">
        <div className="container mx-auto">
          <h2 className="text-3xl font-serif font-bold text-center text-white mb-8 gold-text-outline">
            <Gift className="inline h-8 w-8 mr-2" /> Special Bundle Offers
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* 3-Pack Bundle */}
            {bundle3 && (
              <Card className="border-2 border-amber-500 bg-white overflow-hidden" data-testid="bundle-3-card">
                <CardHeader className="bg-amber-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="font-serif text-xl text-emerald-900">{bundle3.title}</CardTitle>
                      <CardDescription>{bundle3.description}</CardDescription>
                    </div>
                    <Badge className="bg-red-500 text-white">Save ${bundle3.savings.toFixed(2)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-3xl font-bold text-amber-700">${bundle3.price.toFixed(2)}</span>
                      <span className="text-lg text-slate-400 line-through ml-2">${bundle3.original_price.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button 
                    variant="outline"
                    className={`flex-1 ${isBundleInCart("bundle_3") ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : ''}`}
                    onClick={isBundleInCart("bundle_3") ? () => setIsCartOpen(true) : handleBundle3Click}
                    data-testid="bundle-3-add-cart"
                  >
                    {isBundleInCart("bundle_3") ? (
                      <><Check className="mr-1 h-4 w-4" /> In Cart</>
                    ) : (
                      <><Plus className="mr-1 h-4 w-4" /> Add to Cart</>
                    )}
                  </Button>
                  <Button 
                    className="flex-1 bg-emerald-900 hover:bg-emerald-800 gold-text-outline"
                    onClick={handleBundle3Click}
                    disabled={purchaseLoading === "bundle_3"}
                    data-testid="bundle-3-btn"
                  >
                    <ShoppingCart className="mr-1 h-4 w-4" /> Buy Now
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Complete Bundle */}
            {completeBundle && (
              <Card className="border-2 border-amber-500 bg-white overflow-hidden relative" data-testid="bundle-complete-card">
                <div className="absolute top-0 right-0 bg-red-500 text-white px-4 py-1 text-sm font-bold">
                  BEST VALUE
                </div>
                <CardHeader className="bg-amber-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="font-serif text-xl text-emerald-900">{completeBundle.title}</CardTitle>
                      <CardDescription>{completeBundle.description}</CardDescription>
                    </div>
                    <Badge className="bg-red-500 text-white">Save ${completeBundle.savings.toFixed(2)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 mb-3">
                    <div>
                      <span className="text-3xl font-bold text-amber-700">${completeBundle.price.toFixed(2)}</span>
                      <span className="text-lg text-slate-400 line-through ml-2">${completeBundle.original_price.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-emerald-700 font-medium">All 6 packs - 72 total pages!</p>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button 
                    variant="outline"
                    className={`flex-1 ${isBundleInCart("bundle_complete") ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : ''}`}
                    onClick={() => isBundleInCart("bundle_complete") ? setIsCartOpen(true) : handleAddBundleToCart(completeBundle)}
                    data-testid="bundle-complete-add-cart"
                  >
                    {isBundleInCart("bundle_complete") ? (
                      <><Check className="mr-1 h-4 w-4" /> In Cart</>
                    ) : (
                      <><Plus className="mr-1 h-4 w-4" /> Add to Cart</>
                    )}
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-emerald-900 font-bold"
                    onClick={() => handlePurchaseClick("bundle", "bundle_complete")}
                    disabled={purchaseLoading === "bundle_complete"}
                    data-testid="bundle-complete-btn"
                  >
                    {purchaseLoading === "bundle_complete" ? (
                      <><Loader2 className="mr-1 h-4 w-4 animate-spin" /></>
                    ) : (
                      <><ShoppingCart className="mr-1 h-4 w-4" /> Buy Now</>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Individual Packs Grid */}
      <section className="py-16 px-4" data-testid="coloring-packs">
        <div className="container mx-auto">
          <h2 className="text-3xl font-serif font-bold text-center text-slate-900 mb-4">Individual Packs</h2>
          <p className="text-center text-slate-600 mb-12">Or purchase packs individually at $1.00 each</p>
          
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-900" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packs.map((pack) => (
                <Card key={pack.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-slate-200 hover:border-amber-500" data-testid={`pack-card-${pack.id}`}>
                  <div className="relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50" style={{ height: "280px" }}>
                    <img 
                      src={getCoverImage(pack.id)} 
                      alt={pack.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <Badge className="absolute top-3 right-3 bg-amber-500 text-emerald-900 font-bold">
                      {pack.pages} Pages
                    </Badge>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-3 right-3 bg-white/90 hover:bg-white"
                      onClick={() => setPreviewPack(pack)}
                      data-testid={`preview-btn-${pack.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> Preview
                    </Button>
                  </div>
                  <CardHeader>
                    <CardTitle className="font-serif text-slate-900 text-xl">{pack.title}</CardTitle>
                    <CardDescription className="text-slate-600">{pack.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-3xl font-bold text-amber-700">${pack.price.toFixed(2)}</span>
                        <div className="text-xs text-slate-500">ZIP Download</div>
                      </div>
                      <div className="flex items-center gap-1 text-emerald-700">
                        <Check className="h-4 w-4" />
                        <span className="text-sm">Instant Access</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex gap-2">
                    <Button 
                      variant="outline"
                      className={`flex-1 ${isInCart(pack.id) ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : ''}`}
                      onClick={() => handleAddToCart(pack)}
                      disabled={isInCart(pack.id)}
                      data-testid={`add-to-cart-${pack.id}`}
                    >
                      {isInCart(pack.id) ? (
                        <><Check className="mr-2 h-4 w-4" /> In Cart</>
                      ) : (
                        <><Plus className="mr-2 h-4 w-4" /> Add to Cart</>
                      )}
                    </Button>
                    <Button 
                      className="flex-1 bg-emerald-900 hover:bg-emerald-800 gold-text-outline"
                      onClick={() => handlePurchaseClick("pack", pack.id)}
                      disabled={purchaseLoading === pack.id}
                      data-testid={`purchase-btn-${pack.id}`}
                    >
                      {purchaseLoading === pack.id ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /></>
                      ) : (
                        <><ShoppingCart className="mr-2 h-4 w-4" /> Buy Now</>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-serif font-bold text-center text-slate-900 mb-12">Why Our Coloring Pages?</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="h-8 w-8 text-amber-700" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Instant Download</h3>
              <p className="text-slate-600">Get immediate access right after purchase. No waiting!</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-emerald-700" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Email Delivery</h3>
              <p className="text-slate-600">Download link sent to your email for safekeeping.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="h-8 w-8 text-blue-700" />
              </div>
              <h3 className="font-semibold text-lg mb-2">High Quality</h3>
              <p className="text-slate-600">Beautiful, detailed illustrations designed for coloring.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-purple-700" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Faith-Based</h3>
              <p className="text-slate-600">Each page tells a Bible story for meaningful learning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Dialog */}
      <PreviewDialog 
        pack={previewPack} 
        isOpen={!!previewPack} 
        onClose={() => setPreviewPack(null)} 
      />

      {/* Email Collection Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Almost There!</DialogTitle>
            <DialogDescription>
              Enter your email to receive your download link. This ensures you can always access your purchase.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="email">Email Address (optional but recommended)</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                data-testid="email-input"
              />
              <p className="text-xs text-slate-500 mt-1">We'll send your download link to this email.</p>
            </div>
            <Button 
              className="w-full bg-emerald-900 hover:bg-emerald-800"
              onClick={handlePurchase}
              data-testid="proceed-checkout-btn"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Proceed to Checkout
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setEmail("");
                handlePurchase();
              }}
              data-testid="skip-email-btn"
            >
              Skip & Continue Without Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bundle 3 Selection Dialog */}
      <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Choose Your 3 Packs</DialogTitle>
            <DialogDescription>
              Select any 3 coloring packs for just $2.50 (save $0.50!)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {packs.map((pack) => (
                <div 
                  key={pack.id}
                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                    selectedPacks.includes(pack.id) 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-amber-300'
                  } ${selectedPacks.length >= 3 && !selectedPacks.includes(pack.id) ? 'opacity-50' : ''}`}
                  onClick={() => togglePackSelection(pack.id)}
                  data-testid={`select-pack-${pack.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox 
                      checked={selectedPacks.includes(pack.id)}
                      disabled={selectedPacks.length >= 3 && !selectedPacks.includes(pack.id)}
                    />
                    <span className="font-medium text-sm">{pack.title}</span>
                  </div>
                  <img 
                    src={getCoverImage(pack.id)} 
                    alt={pack.title}
                    className="w-full h-24 object-cover rounded"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-slate-600">
                Selected: {selectedPacks.length}/3 packs
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  disabled={selectedPacks.length !== 3}
                  onClick={() => confirmBundle3Selection(true)}
                  data-testid="add-bundle-3-cart-btn"
                >
                  <Plus className="mr-1 h-4 w-4" /> Add to Cart
                </Button>
                <Button 
                  className="bg-emerald-900 hover:bg-emerald-800"
                  disabled={selectedPacks.length !== 3}
                  onClick={() => confirmBundle3Selection(false)}
                  data-testid="confirm-bundle-3-btn"
                >
                  <ShoppingCart className="mr-1 h-4 w-4" /> Buy Now
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ColoringPages;
